import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { ArticleCard } from "../components/ArticleCard";
import { PopularSidebar, LatestSidebar } from "../components/Sidebars";
import AdSlot from "../components/AdSlot";
import { formatDate, formatTime } from "../lib/format";
import { Facebook, Twitter, Link2, MessageCircle, Eye } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [comments, setComments] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", content: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/articles/${slug}`).then(r => {
      setArticle(r.data);
      // SEO
      document.title = `${r.data.seo_title || r.data.title} · Chiquinho`;
      const desc = r.data.seo_description || r.data.subtitle || "";
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) { meta = document.createElement('meta'); meta.name = "description"; document.head.appendChild(meta); }
      meta.content = desc;
    }).catch(() => setArticle(false)).finally(() => setLoading(false));
    api.get(`/articles/${slug}/related`).then(r => setRelated(r.data || []));
    refreshComments();
    window.scrollTo(0, 0);
  // eslint-disable-next-line
  }, [slug]);

  const refreshComments = () => {
    api.get("/comments", { params: { article_id: undefined } });
  };

  useEffect(() => {
    if (article?.id) {
      api.get("/comments", { params: { article_id: article.id } }).then(r => setComments(r.data || []));
    }
  }, [article?.id]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.content) { toast.error("Preencha todos os campos"); return; }
    try {
      await api.post("/comments", { ...form, article_id: article.id });
      setForm({ name: "", email: "", content: "" });
      toast.success("Comentário enviado!");
      const r = await api.get("/comments", { params: { article_id: article.id } });
      setComments(r.data || []);
    } catch (e) {
      toast.error("Erro ao enviar comentário");
    }
  };

  const share = (type) => {
    const url = window.location.href;
    const title = article.title;
    let shareUrl = "";
    if (type === "facebook") shareUrl = `https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    else if (type === "twitter") shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
    else if (type === "whatsapp") shareUrl = `https://wa.me/?text=${encodeURIComponent(title + " " + url)}`;
    else if (type === "copy") { navigator.clipboard.writeText(url); toast.success("Link copiado!"); return; }
    window.open(shareUrl, "_blank");
  };

  if (loading) return <PublicLayout><div className="max-w-3xl mx-auto p-8 font-sans-ui text-zinc-500">Carregando...</div></PublicLayout>;
  if (!article) return <PublicLayout><div className="max-w-3xl mx-auto p-8 text-center"><h1 className="font-serif-display text-3xl">Notícia não encontrada</h1><Link to="/" className="text-red-700 underline">Voltar ao início</Link></div></PublicLayout>;

  return (
    <PublicLayout>
      <article className="max-w-7xl mx-auto px-4 py-8 grid lg:grid-cols-3 gap-10" data-testid="article-page">
        <div className="lg:col-span-2">
          {article.category && (
            <Link to={`/categoria/${article.category.slug}`} className="overline text-red-700" data-testid="article-category-link">{article.category.name}</Link>
          )}
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mt-2" data-testid="article-title">{article.title}</h1>
          {article.subtitle && <p className="font-body text-xl text-zinc-700 mt-3 leading-snug">{article.subtitle}</p>}

          <div className="flex items-center justify-between flex-wrap gap-4 mt-6 pb-4 border-b border-zinc-300 font-sans-ui text-sm text-zinc-600">
            <div className="flex items-center gap-3">
              {article.author && (
                <Link to={`/autor/${article.author.slug}`} className="flex items-center gap-2 hover:text-black" data-testid="article-author-link">
                  {article.author.avatar && <img src={article.author.avatar} alt={article.author.name} className="w-10 h-10 object-cover" />}
                  <div>
                    <p className="font-semibold text-black">{article.author.name}</p>
                    <p className="text-xs">{article.author.bio?.slice(0, 60)}</p>
                  </div>
                </Link>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs">
              {article.published_at && (
                <time dateTime={article.published_at}>{formatDate(article.published_at)} · {formatTime(article.published_at)}</time>
              )}
              <span className="flex items-center gap-1"><Eye size={12}/>{article.views || 0}</span>
            </div>
          </div>

          {/* share */}
          <div className="flex items-center gap-2 mt-4">
            <span className="overline">Compartilhe</span>
            <button onClick={() => share("facebook")} className="p-2 border border-zinc-300 hover:bg-zinc-100" data-testid="share-facebook"><Facebook size={14}/></button>
            <button onClick={() => share("twitter")} className="p-2 border border-zinc-300 hover:bg-zinc-100" data-testid="share-twitter"><Twitter size={14}/></button>
            <button onClick={() => share("whatsapp")} className="p-2 border border-zinc-300 hover:bg-zinc-100" data-testid="share-whatsapp"><MessageCircle size={14}/></button>
            <button onClick={() => share("copy")} className="p-2 border border-zinc-300 hover:bg-zinc-100" data-testid="share-copy"><Link2 size={14}/></button>
          </div>

          {/* cover */}
          {article.cover_image && (
            <figure className="mt-6">
              <img src={article.cover_image} alt={article.title} className="w-full" data-testid="article-cover" />
            </figure>
          )}

          {/* video */}
          {article.video_url && (
            <div className="mt-6 aspect-video bg-black">
              <iframe
                src={article.video_url.replace("watch?v=", "embed/")}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={article.title}
                data-testid="article-video"
              />
            </div>
          )}

          {/* content */}
          <div className="prose-article drop-cap mt-8 max-w-none" data-testid="article-content">
            {article.content?.includes("<") ? (
              <div dangerouslySetInnerHTML={{ __html: article.content }} />
            ) : (
              article.content.split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)
            )}
          </div>

          {/* gallery */}
          {article.images?.length > 0 && (
            <section className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {article.images.map((img, i) => (
                <img key={i} src={img} alt={`${article.title} ${i+1}`} className="w-full aspect-square object-cover" />
              ))}
            </section>
          )}

          {/* tags */}
          {article.tags?.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2" data-testid="article-tags">
              {article.tags.map(t => (
                <Link key={t} to={`/busca?q=${encodeURIComponent(t)}`} className="font-sans-ui text-xs uppercase tracking-widest border border-zinc-300 px-3 py-1 hover:bg-black hover:text-white">#{t}</Link>
              ))}
            </div>
          )}

          <AdSlot placement="inline" />

          {/* related */}
          {related.length > 0 && (
            <section className="mt-12" data-testid="related-section">
              <h2 className="hairline-accent pt-3 mb-6 font-serif-display text-2xl font-bold">Leia também</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {related.map(a => <ArticleCard key={a.id} article={a} size="md" />)}
              </div>
            </section>
          )}

          {/* comments */}
          <section className="mt-12" data-testid="comments-section">
            <h2 className="hairline-accent pt-3 mb-6 font-serif-display text-2xl font-bold">Comentários ({comments.length})</h2>
            <form onSubmit={submitComment} className="border border-zinc-300 p-4 mb-6" data-testid="comment-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <input type="text" placeholder="Seu nome" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="border border-zinc-300 px-3 py-2 font-sans-ui text-sm" data-testid="comment-name" />
                <input type="email" placeholder="Seu e-mail" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="border border-zinc-300 px-3 py-2 font-sans-ui text-sm" data-testid="comment-email" />
              </div>
              <textarea placeholder="Escreva seu comentário..." value={form.content} onChange={e => setForm({...form, content: e.target.value})} rows={4} className="w-full mt-3 border border-zinc-300 px-3 py-2 font-sans-ui text-sm" data-testid="comment-content" />
              <button type="submit" className="mt-3 bg-black text-white px-6 py-2 font-sans-ui text-sm uppercase tracking-widest font-semibold hover:bg-red-700" data-testid="comment-submit">Enviar Comentário</button>
            </form>
            <ul className="space-y-4">
              {comments.map(c => (
                <li key={c.id} className="border-b border-zinc-200 pb-4" data-testid={`comment-${c.id}`}>
                  <p className="font-sans-ui text-sm font-semibold">{c.name}</p>
                  <p className="font-sans-ui text-xs text-zinc-500">{formatDate(c.created_at)}</p>
                  <p className="font-body mt-2">{c.content}</p>
                </li>
              ))}
              {comments.length === 0 && <p className="font-sans-ui text-sm text-zinc-500">Seja o primeiro a comentar.</p>}
            </ul>
          </section>
        </div>

        <aside className="space-y-8">
          <PopularSidebar />
          <AdSlot placement="sidebar" />
          <LatestSidebar />
        </aside>
      </article>
    </PublicLayout>
  );
}
